import { ConditionService } from "@lib/strapi/services/content-types/condition.service";
import { StrapiPages } from "@lib/strapi/pages";
import { StrapiResponse } from "@lib/strapi/dtos/core/response.dto";
import { StrapiRoutes } from "@lib/strapi/routes";
import {
  StrapiTreatmentDTO,
  TreatmentDTO,
} from "@lib/strapi/dtos/content-types/treatment.dto";
import { fetchAPI } from "@lib/strapi/fetch-api";
import { getImageProps, ImageProps } from "@lib/image";
import { parseStrapiContent } from "@lib/strapi/content";

export class TreatmentService {
  static async fromStrapi(
    dto: TreatmentDTO,
    locale: string,
    strapiData?: StrapiTreatmentDTO,
  ): Promise<TreatmentDTO> {
    if (!strapiData) {
      return null;
    }
    if (dto == null) {
      dto = new TreatmentDTO();
    }
    dto.title = strapiData.title;
    dto.slug = strapiData.slug;
    dto.teaser = strapiData.teaser;
    dto.description = await parseStrapiContent(strapiData.description);
    dto.sortIndex = strapiData.sortIndex;
    dto.coverImage = getImageProps(strapiData.coverImage);
    dto.gallery = await Promise.all(
      (strapiData.gallery ?? []).map((item) => getImageProps(item)),
    );
    dto.featuredInHomepageHero = strapiData.featuredInHomepageHero;
    dto.featuredInHomepageCarousel = strapiData.featuredInHomepageCarousel;
    dto.featuredHomepageHeroText = strapiData.featuredHomepageHeroText;
    dto.conditions = await Promise.all(
      (strapiData.conditions ?? []).map(
        async (item) => await ConditionService.fromStrapi(null, locale, item),
      ),
    );
    dto.seo = strapiData.seo;
    return dto;
  }

  static async fetchAll(
    authToken: string,
    locale: string,
  ): Promise<TreatmentDTO[]> {
    const token = (process.env.CMS_API_TOKEN || process.env.STRAPI_API_TOKEN);
    const urlParamsObject = {
      locale,
      sort: ["sortIndex:asc"],
      pagination: { pageSize: 200 },
    };
    const response: StrapiResponse<StrapiTreatmentDTO[]> = await fetchAPI(
      StrapiRoutes.treatments(),
      urlParamsObject,
      token,
    );
    const dto = new TreatmentDTO();
    return await Promise.all(
      (response.data ?? []).map(
        async (item) =>
          await TreatmentService.fromStrapi(new TreatmentDTO(), locale, item),
      ),
    );
  }

  static async fetchBySlug(
    authToken: string,
    locale: string,
    slug?: string,
  ): Promise<TreatmentDTO> {
    const token = (process.env.CMS_API_TOKEN || process.env.STRAPI_API_TOKEN);
    const urlParamsObject = { locale, filters: { slug: slug } };
    const response: StrapiResponse<StrapiTreatmentDTO[]> = await fetchAPI(
      StrapiRoutes.treatments(),
      urlParamsObject,
      token,
    );
    const dto = new TreatmentDTO();
    if ((response.data ?? []).length === 0) {
      return null;
    }
    return await TreatmentService.fromStrapi(
      dto,
      locale,
      (response.data ?? [])[0],
    );
  }

  static async fetchHomepageCarousel(
    authToken: string,
    locale: string,
  ): Promise<TreatmentDTO[]> {
    const token = (process.env.CMS_API_TOKEN || process.env.STRAPI_API_TOKEN);
    const urlParamsObject = {
      locale,
      filters: { featuredInHomepageCarousel: true },
      sort: ["sortIndex:asc"],
      pagination: { pageSize: 50 },
    };
    const response: StrapiResponse<StrapiTreatmentDTO[]> = await fetchAPI(
      StrapiRoutes.treatments(),
      urlParamsObject,
      token,
    );
    const dto = new TreatmentDTO();
    return await Promise.all(
      (response.data ?? []).map(
        async (item) =>
          await TreatmentService.fromStrapi(new TreatmentDTO(), locale, item),
      ),
    );
  }

  static async fetchHomepageHero(
    authToken: string,
    locale: string,
  ): Promise<TreatmentDTO[]> {
    const token = (process.env.CMS_API_TOKEN || process.env.STRAPI_API_TOKEN);
    const urlParamsObject = {
      locale,
      filters: { featuredInHomepageHero: true },
      sort: ["sortIndex:asc"],
      pagination: { pageSize: 50 },
    };
    const response: StrapiResponse<StrapiTreatmentDTO[]> = await fetchAPI(
      StrapiRoutes.treatments(),
      urlParamsObject,
      token,
    );
    const dto = new TreatmentDTO();
    return await Promise.all(
      (response.data ?? []).map(
        async (item) =>
          await TreatmentService.fromStrapi(new TreatmentDTO(), locale, item),
      ),
    );
  }
}
